import { useState } from "react";
import { Plus, Star, Trash2 } from "lucide-react";

import { EmptyTable, ErrorTable, LoadingTable } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/core/components/ui/table";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useDeleteBeneficiary } from "@/features/wallet/api/use-delete-beneficiary";
import { useWalletBeneficiaries } from "@/features/wallet/api/use-wallet-beneficiaries";
import type { BeneficiaryDto } from "@/features/wallet/api/dto";
import { BeneficiaryFormDialog } from "@/features/wallet/components/beneficiaries/beneficiary-form-dialog";

/**
 * Bénéficiaires d'un portefeuille (ressource 3.3) : liste
 * (`GET /Beneficiaries/wallet/{walletId}`), ajout via dialog et suppression
 * (`DELETE /Beneficiaries/{id}`) avec confirmation — numéros de compte en
 * `NoTranslate` (règle notranslate).
 */
export function BeneficiariesSection({ walletId }: { walletId: string }) {
  const { t } = useTypedTranslation();
  // Les clés wallet.beneficiaries.* vivent dans
  // features/wallet/i18n/beneficiaries.json en attendant leur fusion dans
  // src/locales par le coordinateur.
  const tBeneficiary = (key: string, params?: Record<string, unknown>) =>
    t(key as TFunctionType, params as Record<string, never>);

  const { data: beneficiaries, isLoading, error, refetch } =
    useWalletBeneficiaries(walletId);
  const deleteBeneficiary = useDeleteBeneficiary(walletId);

  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BeneficiaryDto | undefined>();

  const onDelete = () => {
    if (!deleteTarget?.id) return;
    deleteBeneficiary.mutate(deleteTarget.id, {
      onSuccess: () => {
        notifSilence(tBeneficiary("wallet.beneficiaries.feedback.deleted"));
        setDeleteTarget(undefined);
      },
      onError: (mutationError) => notifSilenceError(mutationError.message),
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">
          {tBeneficiary("wallet.beneficiaries.title")}
        </CardTitle>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {tBeneficiary("wallet.beneficiaries.add")}
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {tBeneficiary("wallet.beneficiaries.columns.label")}
              </TableHead>
              <TableHead>
                {tBeneficiary("wallet.beneficiaries.columns.type")}
              </TableHead>
              <TableHead>
                {tBeneficiary("wallet.beneficiaries.columns.institutionCode")}
              </TableHead>
              <TableHead>
                {tBeneficiary("wallet.beneficiaries.columns.accountNumber")}
              </TableHead>
              <TableHead>
                {tBeneficiary("wallet.beneficiaries.columns.isFavorite")}
              </TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <LoadingTable numberOfColumns={6} />
            ) : error ? (
              <ErrorTable
                error={{
                  message: error.message,
                  refetch: () => void refetch(),
                }}
              />
            ) : !beneficiaries || beneficiaries.length === 0 ? (
              <EmptyTable />
            ) : (
              beneficiaries.map((beneficiary) => {
                const deleting =
                  deleteBeneficiary.isPending &&
                  deleteBeneficiary.variables === beneficiary.id;
                return (
                  <TableRow key={beneficiary.id}>
                    <TableCell>
                      <NoTranslate as="span" className="font-medium">
                        {beneficiary.label || "—"}
                      </NoTranslate>
                    </TableCell>
                    <TableCell>
                      <NoTranslate as="span">
                        <Badge variant="secondary">
                          {beneficiary.type || "—"}
                        </Badge>
                      </NoTranslate>
                    </TableCell>
                    <TableCell>
                      <NoTranslate as="span">
                        {beneficiary.institutionCode || "—"}
                      </NoTranslate>
                    </TableCell>
                    <TableCell>
                      <NoTranslate as="span" className="font-mono text-xs">
                        {beneficiary.accountNumber || "—"}
                      </NoTranslate>
                    </TableCell>
                    <TableCell>
                      {beneficiary.isFavorite ? (
                        <Star
                          className="h-4 w-4 text-accent-dark"
                          aria-label={t("yes")}
                        />
                      ) : (
                        <span className="text-muted-foreground">
                          {t("no")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        title={tBeneficiary("wallet.beneficiaries.delete.confirm")}
                        disabled={deleting || !beneficiary.id}
                        onClick={() => setDeleteTarget(beneficiary)}
                      >
                        {deleting ? (
                          <ButtonSpinner size="sm" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-destructive" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>

      <BeneficiaryFormDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        walletId={walletId}
      />

      <MutationConfirmPreview
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(undefined);
        }}
        data={{
          id: deleteTarget?.id,
          label: deleteTarget?.label,
          type: deleteTarget?.type,
          accountNumber: deleteTarget?.accountNumber,
        }}
        title={tBeneficiary("wallet.beneficiaries.delete.title")}
        description={tBeneficiary("wallet.beneficiaries.delete.description", {
          label: deleteTarget?.label ?? "",
        })}
        confirmLabel={tBeneficiary("wallet.beneficiaries.delete.confirm")}
        t={tBeneficiary}
        pending={deleteBeneficiary.isPending}
        onConfirm={onDelete}
      />
    </Card>
  );
}
